/**
 * CTO Command Center Type Definitions
 * Enterprise Architecture Suite with unified entity repository
 */

// ============================================================================
// ENTITY TYPES
// ============================================================================

/**
 * All entity types that can be tracked across tools
 */
export type EntityType =
  // Technology Layer
  | 'technology'        // Software technologies, libraries, frameworks
  | 'infrastructure'    // Hardware, cloud resources, networks
  | 'application'       // Application components, services
  | 'data_store'        // Databases, caches, storage systems
  | 'api'               // API interfaces, endpoints

  // Business Layer
  | 'team'              // Teams, departments
  | 'vendor'            // Third-party vendors, suppliers
  | 'process'           // Business processes, workflows
  | 'capability'        // Business capabilities
  | 'product'           // Products, offerings
  | 'stakeholder'       // Key stakeholders

  // Compliance & Governance
  | 'compliance_framework'  // GDPR, SOC2, HIPAA, etc.
  | 'control'           // Security controls
  | 'risk'              // Risk items
  | 'policy'            // Policies, guidelines

  // Architecture
  | 'architecture_decision'  // ADRs
  | 'tech_debt'         // Technical debt items
  | 'migration_plan'    // Migration plans

  // Operations
  | 'incident'          // Incidents, outages
  | 'sla'               // Service level agreements
  | 'metric';           // Performance metrics, KPIs

/**
 * Entity status values
 */
export type EntityStatus = 'active' | 'archived' | 'deprecated';

/**
 * Relationship types between entities
 */
export type RelationshipType =
  | 'depends_on'    // Source depends on target
  | 'owns'          // Source owns/is responsible for target
  | 'uses'          // Source uses target
  | 'provides'      // Source provides target
  | 'manages'       // Source manages target
  | 'contains'      // Source contains target
  | 'implements'    // Source implements target
  | 'supports'      // Source supports target
  | 'monitors'      // Source monitors target
  | 'secures';      // Source secures target

// ============================================================================
// CORE ENTITIES
// ============================================================================

/**
 * Main entity interface
 */
export interface Entity {
  id: string;
  workspace_id: string;
  entity_type: EntityType;
  name: string;
  description?: string;
  canonical_name?: string;  // Generated, lowercase alphanumeric
  external_id?: string;
  external_source?: string;
  metadata: EntityMetadata;
  tags: string[];
  status: EntityStatus;
  created_by: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Entity metadata (flexible JSONB field)
 */
export interface EntityMetadata {
  // Common fields
  website?: string;
  repository?: string;
  documentation?: string;
  icon?: string;
  color?: string;

  // Technology-specific
  category?: string;
  version?: string;
  license?: 'open-source' | 'commercial' | 'freemium' | 'enterprise-only';
  pricing?: {
    model?: string;
    monthlyCost?: number;
    annualCost?: number;
  };
  technical?: {
    languages?: string[];
    platforms?: string[];
    scalability?: string;
    performance?: string;
  };

  // Team-specific
  size?: number;
  lead?: string;
  slackChannel?: string;
  email?: string;
  oncall?: string;
  costCenter?: string;

  // Vendor-specific
  contractExpiry?: string;
  tier?: 'critical' | 'important' | 'standard';
  annualSpend?: number;
  primaryContact?: string;
  soc2Report?: string;
  lastReview?: string;

  // Compliance-specific
  fullName?: string;
  applicability?: string[];
  renewalPeriod?: string;
  lastAudit?: string;
  nextAudit?: string;
  auditor?: string;

  // Generic extensible fields
  [key: string]: unknown;
}

/**
 * Entity for creation (without server-generated fields)
 */
export interface CreateEntityParams {
  workspace_id: string;
  entity_type: EntityType;
  name: string;
  description?: string;
  external_id?: string;
  external_source?: string;
  metadata?: Partial<EntityMetadata>;
  tags?: string[];
  status?: EntityStatus;
}

/**
 * Entity for updates (all fields optional except id)
 */
export interface UpdateEntityParams {
  name?: string;
  description?: string;
  metadata?: Partial<EntityMetadata>;
  tags?: string[];
  status?: EntityStatus;
}

// ============================================================================
// ENTITY LINKS
// ============================================================================

/**
 * Relationship between two entities
 */
export interface EntityLink {
  id: string;
  workspace_id: string;
  source_entity_id: string;
  target_entity_id: string;
  relationship_type: RelationshipType;
  strength: number;  // 0-1
  metadata: Record<string, unknown>;
  created_by?: string;
  created_at: string;
}

/**
 * Entity link for creation
 */
export interface CreateEntityLinkParams {
  workspace_id: string;
  source_entity_id: string;
  target_entity_id: string;
  relationship_type: RelationshipType;
  strength?: number;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// ARCHIMATE MAPPINGS
// ============================================================================

/**
 * Mapping type between entity and ArchiMate element
 */
export type MappingType = 'exact' | 'represents' | 'contains';

/**
 * Sync direction for ArchiMate mapping
 */
export type SyncDirection = 'to_archimate' | 'from_archimate' | 'bidirectional';

/**
 * Sync status for ArchiMate mapping
 */
export type SyncStatus = 'pending' | 'synced' | 'conflict' | 'error';

/**
 * Mapping between entity and ArchiMate element
 */
export interface ArchiMateMapping {
  id: string;
  entity_id: string;
  archimate_workspace_id: string;
  archimate_diagram_id: string;
  archimate_element_id: string;
  mapping_type: MappingType;
  sync_direction: SyncDirection;
  sync_status: SyncStatus;
  sync_error?: string;
  last_synced_at?: string;
  created_at: string;
  updated_at: string;
}

/**
 * ArchiMate mapping for creation
 */
export interface CreateArchiMateMappingParams {
  entity_id: string;
  archimate_workspace_id: string;
  archimate_diagram_id: string;
  archimate_element_id: string;
  mapping_type?: MappingType;
  sync_direction?: SyncDirection;
}

// ============================================================================
// ASSESSMENTS
// ============================================================================

/**
 * Tool category for assessments
 */
export type ToolCategory =
  | 'compliance'   // Compliance assessments (GDPR, SOC2, etc.)
  | 'maturity'     // Maturity assessments (CI/CD, Architecture)
  | 'calculator'   // Calculators (Cloud Cost, ROI)
  | 'planner'      // Planners (Sprint, Scenario)
  | 'analyzer'     // Analyzers (Tech Debt, Risk)
  | 'generator';   // Generators (SLA, RFP)

/**
 * Assessment status
 */
export type AssessmentStatus = 'draft' | 'in_progress' | 'completed' | 'archived';

/**
 * Assessment result from a tool
 */
export interface Assessment {
  id: string;
  workspace_id: string;
  tool_id: string;
  tool_category: ToolCategory;
  name: string;
  version: number;
  score_overall?: number;  // 0-100
  scores: Record<string, number>;
  answers: Record<string, unknown>;
  gaps: AssessmentGap[];
  recommendations: string[];
  metadata: Record<string, unknown>;
  status: AssessmentStatus;
  completed_at?: string;
  expires_at?: string;
  created_by: string;
  updated_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Assessment gap item
 */
export interface AssessmentGap {
  id: string;
  category?: string;
  title: string;
  description?: string;
  severity?: 'low' | 'medium' | 'high' | 'critical';
  remediation?: string;
}

/**
 * Assessment for creation
 */
export interface CreateAssessmentParams {
  workspace_id: string;
  tool_id: string;
  tool_category: ToolCategory;
  name?: string;
  score_overall?: number;
  scores?: Record<string, number>;
  answers?: Record<string, unknown>;
  gaps?: AssessmentGap[];
  recommendations?: string[];
  metadata?: Record<string, unknown>;
  status?: AssessmentStatus;
}

/**
 * Assessment for updates
 */
export interface UpdateAssessmentParams {
  name?: string;
  score_overall?: number;
  scores?: Record<string, number>;
  answers?: Record<string, unknown>;
  gaps?: AssessmentGap[];
  recommendations?: string[];
  metadata?: Record<string, unknown>;
  status?: AssessmentStatus;
}

/**
 * Link between assessment and entity
 */
export interface AssessmentEntity {
  assessment_id: string;
  entity_id: string;
  role: 'subject' | 'related' | 'owner' | 'affected';
}

// ============================================================================
// MATCH SUGGESTIONS
// ============================================================================

/**
 * Source/target type for matching
 */
export type MatchSourceType = 'entity' | 'archimate' | 'assessment';
export type MatchTargetType = 'entity' | 'archimate';

/**
 * Match suggestion status
 */
export type MatchStatus = 'pending' | 'accepted' | 'rejected' | 'expired';

/**
 * Reason for a match suggestion
 */
export interface MatchReason {
  type: 'name_exact' | 'name_fuzzy' | 'type_compatible' | 'url_match' | 'tag_overlap' | 'metadata_match' | 'same_team';
  score: number;  // 0-1
  detail: string;
}

/**
 * Match suggestion between entities/elements
 */
export interface MatchSuggestion {
  id: string;
  workspace_id: string;
  source_type: MatchSourceType;
  source_id: string;
  source_name: string;
  target_type: MatchTargetType;
  target_id: string;
  target_name: string;
  confidence: number;  // 0-1
  match_reasons: MatchReason[];
  status: MatchStatus;
  reviewed_by?: string;
  reviewed_at?: string;
  created_at: string;
  expires_at?: string;
}

// ============================================================================
// ACTIVITY LOG
// ============================================================================

/**
 * Activity action types
 */
export type ActivityAction =
  | 'entity_created' | 'entity_updated' | 'entity_deleted'
  | 'link_created' | 'link_deleted'
  | 'mapping_created' | 'mapping_deleted' | 'mapping_synced'
  | 'assessment_created' | 'assessment_updated' | 'assessment_completed' | 'assessment_deleted'
  | 'match_accepted' | 'match_rejected'
  // Phase 1: SplitCause integration actions
  | 'incident_linked' | 'incident_unlinked'
  | 'reliability_computed'
  | 'status_propagated'
  | 'impact_analyzed';

/**
 * Activity log entry
 */
export interface ActivityLogEntry {
  id: string;
  workspace_id: string;
  user_id: string;
  action: ActivityAction;
  object_type: string;
  object_id: string;
  object_name?: string;
  changes?: Record<string, unknown>;
  created_at: string;
}

// ============================================================================
// DASHBOARD & STATS
// ============================================================================

/**
 * Workspace statistics for dashboard
 */
export interface WorkspaceStats {
  total_entities: number;
  active_entities: number;
  entity_types: number;
  total_links: number;
  total_mappings: number;
  total_assessments: number;
  pending_matches: number;
  last_activity?: string;
}

/**
 * Entity count by type
 */
export interface EntityCountByType {
  entity_type: EntityType;
  count: number;
}

/**
 * Assessment count by tool/category
 */
export interface AssessmentCountByTool {
  tool_id: string;
  tool_category: ToolCategory;
  count: number;
  avg_score?: number;
}

/**
 * Compliance health summary
 */
export interface ComplianceHealth {
  framework: string;
  score: number;
  last_assessment?: string;
  gaps_count: number;
  status: 'compliant' | 'partial' | 'non-compliant' | 'not-assessed';
}

/**
 * Full dashboard data
 */
export interface DashboardData {
  stats: WorkspaceStats;
  entities_by_type: EntityCountByType[];
  assessments_by_tool: AssessmentCountByTool[];
  compliance_health: ComplianceHealth[];
  recent_activity: ActivityLogEntry[];
  pending_matches: MatchSuggestion[];
}

// ============================================================================
// API RESPONSES
// ============================================================================

/**
 * Paginated list response
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/**
 * Entity list filters
 */
export interface EntityFilters {
  entity_type?: EntityType | EntityType[];
  status?: EntityStatus;
  tags?: string[];
  search?: string;
  external_source?: string;
  created_by?: string;
}

/**
 * Assessment list filters
 */
export interface AssessmentFilters {
  tool_id?: string;
  tool_category?: ToolCategory | ToolCategory[];
  status?: AssessmentStatus;
  created_by?: string;
  from_date?: string;
  to_date?: string;
}

// ============================================================================
// TYPE MAPPING HELPERS
// ============================================================================

/**
 * ArchiMate element types that map to each entity type
 */
export const ENTITY_TO_ARCHIMATE_TYPES: Record<EntityType, string[]> = {
  technology: ['system-software', 'artifact', 'node'],
  infrastructure: ['node', 'device', 'communication-network', 'path', 'facility'],
  application: ['application-component', 'application-service', 'application-function'],
  data_store: ['data-object', 'artifact'],
  api: ['application-interface', 'technology-interface'],
  team: ['business-actor', 'business-role', 'business-collaboration'],
  vendor: ['business-actor'],
  process: ['business-process', 'business-function', 'business-interaction'],
  capability: ['capability', 'resource'],
  product: ['product', 'business-service'],
  stakeholder: ['stakeholder'],
  compliance_framework: ['requirement', 'constraint'],
  control: ['requirement'],
  risk: ['assessment'],
  policy: ['principle'],
  architecture_decision: ['requirement'],
  tech_debt: ['gap', 'work-package'],
  migration_plan: ['plateau', 'work-package'],
  incident: ['business-event'],
  sla: ['contract'],
  metric: ['value'],
};

/**
 * Entity type display names
 */
export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  technology: 'Technology',
  infrastructure: 'Infrastructure',
  application: 'Application',
  data_store: 'Data Store',
  api: 'API',
  team: 'Team',
  vendor: 'Vendor',
  process: 'Process',
  capability: 'Capability',
  product: 'Product',
  stakeholder: 'Stakeholder',
  compliance_framework: 'Compliance Framework',
  control: 'Control',
  risk: 'Risk',
  policy: 'Policy',
  architecture_decision: 'Architecture Decision',
  tech_debt: 'Technical Debt',
  migration_plan: 'Migration Plan',
  incident: 'Incident',
  sla: 'SLA',
  metric: 'Metric',
};

/**
 * Entity type categories for grouping
 */
export const ENTITY_TYPE_CATEGORIES: Record<string, EntityType[]> = {
  'Technology': ['technology', 'infrastructure', 'application', 'data_store', 'api'],
  'Business': ['team', 'vendor', 'process', 'capability', 'product', 'stakeholder'],
  'Governance': ['compliance_framework', 'control', 'risk', 'policy'],
  'Architecture': ['architecture_decision', 'tech_debt', 'migration_plan'],
  'Operations': ['incident', 'sla', 'metric'],
};

/**
 * Tool IDs for compliance tools
 */
export const COMPLIANCE_TOOL_IDS = [
  'gdpr-compliance',
  'soc2-readiness',
  'hipaa-compliance',
  'iso27001-gap-analysis',
  'nist-csf-assessment',
  'pci-dss-checker',
  'dora-compliance',
  'nis2-readiness',
  'ccpa-cpra-compliance',
  'eu-ai-act-compliance',
  'vendor-risk-assessment',
  'wcag-compliance',
  'ai-ethics-assessment',
  'business-continuity',
] as const;

export type ComplianceToolId = typeof COMPLIANCE_TOOL_IDS[number];

// ============================================================================
// PHASE 1: SPLITCAUSE INTEGRATION & DEPENDENCY ANALYSIS
// ============================================================================

/**
 * Role of an entity in an incident
 */
export type IncidentRole = 'affected' | 'root_cause' | 'contributing' | 'observer';

/**
 * Severity levels for incidents
 */
export type IncidentSeverity = 'low' | 'medium' | 'high' | 'critical';

/**
 * Link between a Command Center entity and a SplitCause incident
 */
export interface EntityIncident {
  id: string;
  entity_id: string;
  incident_id: string;
  graph_id?: string;
  role: IncidentRole;
  impact_score?: number;
  severity?: IncidentSeverity;
  started_at?: string;
  resolved_at?: string;
  duration_minutes?: number;
  notes?: string;
  metadata: Record<string, unknown>;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Parameters for creating an entity-incident link
 */
export interface CreateEntityIncidentParams {
  entity_id: string;
  incident_id: string;
  graph_id?: string;
  role: IncidentRole;
  impact_score?: number;
  severity?: IncidentSeverity;
  started_at?: string;
  resolved_at?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Reliability trend direction
 */
export type ReliabilityTrend = 'improving' | 'stable' | 'degrading';

/**
 * Data quality indicator for reliability metrics
 */
export type DataQuality = 'insufficient' | 'low' | 'medium' | 'high';

/**
 * Computed reliability metrics for an entity
 */
export interface EntityReliability {
  entity_id: string;
  mtbf_hours?: number;        // Mean Time Between Failures
  mttr_hours?: number;        // Mean Time To Recovery
  availability_score?: number; // 0-1
  incident_count_7d: number;
  incident_count_30d: number;
  incident_count_90d: number;
  incident_count_365d: number;
  incidents_by_severity: Record<IncidentSeverity, number>;
  last_incident_at?: string;
  last_failure_at?: string;
  uptime_streak_days: number;
  trend?: ReliabilityTrend;
  trend_score?: number;       // -1 to 1
  computed_at: string;
  data_quality: DataQuality;
}

/**
 * Propagation mode for status updates
 */
export type PropagationMode = 'automatic' | 'manual' | 'notify';

/**
 * Direction for propagation
 */
export type PropagationDirection = 'upstream' | 'downstream' | 'both';

/**
 * Rule for automatic status propagation
 */
export interface StatusPropagationRule {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  source_entity_type: EntityType;
  source_status: 'degraded' | 'failed';
  target_relationship_type: RelationshipType;
  target_direction: PropagationDirection;
  propagation_mode: PropagationMode;
  severity_threshold?: IncidentSeverity;
  max_depth?: number;
  is_active: boolean;
  priority: number;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Parameters for creating a status propagation rule
 */
export interface CreateStatusPropagationRuleParams {
  workspace_id: string;
  name: string;
  description?: string;
  source_entity_type: EntityType;
  source_status: 'degraded' | 'failed';
  target_relationship_type: RelationshipType;
  target_direction?: PropagationDirection;
  propagation_mode?: PropagationMode;
  severity_threshold?: IncidentSeverity;
  max_depth?: number;
  is_active?: boolean;
  priority?: number;
}

/**
 * Impact analysis type
 */
export type ImpactAnalysisType = 'upstream' | 'downstream' | 'blast_radius' | 'critical_path';

/**
 * Affected entity in impact analysis
 */
export interface AffectedEntity {
  id: string;
  name: string;
  entity_type: EntityType;
  depth: number;
  impact_score: number;
  relationship_path: string[];  // IDs of entities in the path
  is_critical?: boolean;
  is_spof?: boolean;
}

/**
 * Cached impact analysis results
 */
export interface ImpactAnalysisResult {
  id: string;
  workspace_id: string;
  source_entity_id: string;
  analysis_type: ImpactAnalysisType;
  affected_entities: AffectedEntity[];
  critical_paths: string[][];  // Arrays of entity IDs
  summary: {
    total_affected: number;
    by_type: Record<EntityType, number>;
    by_depth: Record<number, number>;
    max_depth: number;
    avg_impact_score: number;
  };
  computed_at: string;
  expires_at: string;
  computation_time_ms?: number;
}

/**
 * Dependency metrics for an entity
 */
export interface DependencyMetrics {
  id: string;
  workspace_id: string;
  entity_id: string;
  upstream_count: number;
  downstream_count: number;
  total_dependencies: number;
  is_single_point_of_failure: boolean;
  criticality_score?: number;   // 0-1
  fan_out_score?: number;       // 0-1
  has_redundancy: boolean;
  redundancy_count: number;
  computed_at: string;
}

/**
 * Status propagation log entry
 */
export interface StatusPropagationLog {
  id: string;
  workspace_id: string;
  rule_id?: string;
  source_entity_id: string;
  source_status: string;
  incident_id?: string;
  target_entity_id: string;
  previous_status?: string;
  new_status?: string;
  action: 'updated' | 'notified' | 'blocked' | 'manual_required';
  action_by?: string;
  action_at?: string;
  created_at: string;
}

/**
 * Entity with reliability data (joined view)
 */
export interface EntityWithReliability extends Entity {
  reliability?: EntityReliability;
  dependency_metrics?: DependencyMetrics;
  incident_count?: number;
}

/**
 * Critical entity summary (from view)
 */
export interface CriticalEntity {
  id: string;
  workspace_id: string;
  entity_type: EntityType;
  name: string;
  status: EntityStatus;
  availability_score?: number;
  incident_count_30d?: number;
  is_single_point_of_failure: boolean;
  criticality_score?: number;
  downstream_count?: number;
  risk_reason: string;
}

/**
 * "What If" scenario request
 */
export interface WhatIfScenario {
  entity_id: string;
  scenario_type: 'failure' | 'deprecation' | 'migration';
  new_status?: EntityStatus;
  include_indirect?: boolean;
  max_depth?: number;
}

/**
 * "What If" scenario result
 */
export interface WhatIfResult {
  scenario: WhatIfScenario;
  affected_entities: AffectedEntity[];
  risk_score: number;  // 0-1
  recommendations: string[];
  computed_at: string;
}

// ============================================================================
// PHASE 2: TEAM OWNERSHIP, RACI & TECH RADAR
// ============================================================================

/**
 * RACI role types
 */
export type OwnershipRole = 'responsible' | 'accountable' | 'consulted' | 'informed';

/**
 * Entity ownership record
 */
export interface EntityOwnership {
  id: string;
  workspace_id: string;
  entity_id: string;
  owner_entity_id?: string;  // Optional link to team/stakeholder entity
  owner_email: string;
  owner_name: string;
  role: OwnershipRole;
  is_primary: boolean;
  start_date?: string;
  end_date?: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Parameters for creating an ownership record
 */
export interface CreateEntityOwnershipParams {
  entity_id: string;
  owner_entity_id?: string;
  owner_email: string;
  owner_name: string;
  role?: OwnershipRole;
  is_primary?: boolean;
  start_date?: string;
  end_date?: string;
  notes?: string;
}

/**
 * Tech radar quadrants
 */
export type TechRadarQuadrant = 'techniques' | 'tools' | 'platforms' | 'languages_frameworks';

/**
 * Tech radar rings (adoption levels)
 */
export type TechRadarRing = 'adopt' | 'trial' | 'assess' | 'hold';

/**
 * Tech radar movement direction
 */
export type TechRadarMovement = 'up' | 'down' | 'none' | 'new';

/**
 * Tech radar entry
 */
export interface TechRadarEntry {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  quadrant: TechRadarQuadrant;
  ring: TechRadarRing;
  entity_id?: string;
  previous_ring?: TechRadarRing | 'new';
  movement: TechRadarMovement;
  movement_date?: string;
  movement_reason?: string;
  rationale?: string;
  tags: string[];
  url?: string;
  is_active: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Parameters for creating a tech radar entry
 */
export interface CreateTechRadarEntryParams {
  name: string;
  description?: string;
  quadrant: TechRadarQuadrant;
  ring: TechRadarRing;
  entity_id?: string;
  rationale?: string;
  tags?: string[];
  url?: string;
  movement_reason?: string;
}

/**
 * Parameters for updating a tech radar entry
 */
export interface UpdateTechRadarEntryParams {
  name?: string;
  description?: string;
  quadrant?: TechRadarQuadrant;
  ring?: TechRadarRing;
  rationale?: string;
  tags?: string[];
  url?: string;
  movement_reason?: string;
  is_active?: boolean;
}

/**
 * Tech radar snapshot
 */
export interface TechRadarSnapshot {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  snapshot_date: string;
  entries_data: TechRadarEntry[];
  entry_count: number;
  adopt_count: number;
  trial_count: number;
  assess_count: number;
  hold_count: number;
  created_by?: string;
  created_at: string;
}

/**
 * Tech radar summary stats
 */
export interface TechRadarSummary {
  workspace_id: string;
  quadrant: TechRadarQuadrant;
  ring: TechRadarRing;
  entry_count: number;
  moving_up: number;
  moving_down: number;
  new_entries: number;
}

/**
 * Team topology types (from Team Topologies book)
 */
export type TeamTopologyType =
  | 'stream_aligned'       // Aligned to a flow of work from a business domain
  | 'platform'             // Enables stream-aligned teams
  | 'enabling'             // Helps other teams with specialized capabilities
  | 'complicated_subsystem';  // Handles complex subsystems requiring specialist knowledge

/**
 * Team interaction modes (from Team Topologies book)
 */
export type TeamInteractionMode =
  | 'collaboration'   // Working closely together
  | 'x_as_a_service'  // Consuming or providing a service
  | 'facilitating';   // Helping another team

/**
 * Team with topology metadata
 */
export interface TeamWithTopology {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  status: EntityStatus;
  team_topology?: TeamTopologyType;
  team_size?: number;
  cognitive_load?: 'low' | 'medium' | 'high';
  interaction_mode?: TeamInteractionMode;
  owned_entities_count: number;
  interaction_count: number;
}

/**
 * RACI matrix entry
 */
export interface RACIMatrixEntry {
  workspace_id: string;
  entity_id: string;
  entity_name: string;
  entity_type: EntityType;
  owner_name: string;
  owner_email: string;
  role: OwnershipRole;
  is_primary: boolean;
  start_date?: string;
  end_date?: string;
  ownership_status: 'active' | 'pending' | 'expired';
}

/**
 * Ownership coverage stats
 */
export interface OwnershipCoverageSummary {
  workspace_id: string;
  entity_type: EntityType;
  total_entities: number;
  entities_with_primary_owner: number;
  entities_with_accountable: number;
  ownership_coverage_pct: number;
}

/**
 * Tech radar quadrant labels
 */
export const TECH_RADAR_QUADRANT_LABELS: Record<TechRadarQuadrant, string> = {
  techniques: 'Techniques',
  tools: 'Tools',
  platforms: 'Platforms',
  languages_frameworks: 'Languages & Frameworks',
};

/**
 * Tech radar ring labels with colors
 */
export const TECH_RADAR_RING_CONFIG: Record<TechRadarRing, { label: string; color: string; bgColor: string }> = {
  adopt: { label: 'Adopt', color: 'text-green-400', bgColor: 'bg-green-900/50' },
  trial: { label: 'Trial', color: 'text-blue-400', bgColor: 'bg-blue-900/50' },
  assess: { label: 'Assess', color: 'text-yellow-400', bgColor: 'bg-yellow-900/50' },
  hold: { label: 'Hold', color: 'text-red-400', bgColor: 'bg-red-900/50' },
};

/**
 * Team topology type labels
 */
export const TEAM_TOPOLOGY_LABELS: Record<TeamTopologyType, string> = {
  stream_aligned: 'Stream-Aligned',
  platform: 'Platform',
  enabling: 'Enabling',
  complicated_subsystem: 'Complicated Subsystem',
};

/**
 * RACI role labels
 */
export const RACI_ROLE_LABELS: Record<OwnershipRole, { label: string; description: string; color: string }> = {
  responsible: {
    label: 'Responsible',
    description: 'Does the work to complete the task',
    color: 'text-blue-400',
  },
  accountable: {
    label: 'Accountable',
    description: 'Ultimately answerable for the task',
    color: 'text-purple-400',
  },
  consulted: {
    label: 'Consulted',
    description: 'Provides input and expertise',
    color: 'text-yellow-400',
  },
  informed: {
    label: 'Informed',
    description: 'Kept up-to-date on progress',
    color: 'text-gray-400',
  },
};

// ============================================================================
// PHASE 3: COST TRACKING & DOCUMENTATION MANAGEMENT
// ============================================================================

/**
 * Cost types
 */
export type CostType = 'infrastructure' | 'licensing' | 'labor' | 'support' | 'other';

/**
 * Cost period types
 */
export type CostPeriod = 'monthly' | 'yearly' | 'one_time';

/**
 * Entity cost record
 */
export interface EntityCost {
  id: string;
  workspace_id: string;
  entity_id: string;
  cost_type: CostType;
  amount: number;
  currency: string;
  period: CostPeriod;
  effective_from?: string;
  effective_to?: string;
  source?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Parameters for creating a cost record
 */
export interface CreateEntityCostParams {
  entity_id: string;
  cost_type: CostType;
  amount: number;
  currency?: string;
  period?: CostPeriod;
  effective_from?: string;
  effective_to?: string;
  source?: string;
  notes?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Cost allocation record
 */
export interface CostAllocation {
  id: string;
  workspace_id: string;
  allocation_type: 'team' | 'product' | 'capability' | 'department';
  target_entity_id?: string;
  target_name: string;
  period_start: string;
  period_end: string;
  total_amount: number;
  infrastructure_amount: number;
  licensing_amount: number;
  labor_amount: number;
  support_amount: number;
  other_amount: number;
  currency: string;
  entity_count: number;
  computed_at: string;
  created_by?: string;
}

/**
 * Entity cost summary (aggregated view)
 */
export interface EntityCostSummary {
  workspace_id: string;
  entity_id: string;
  entity_name: string;
  entity_type: EntityType;
  monthly_cost: number;
  yearly_cost: number;
  one_time_cost: number;
  infrastructure_monthly: number;
  licensing_monthly: number;
  labor_monthly: number;
  cost_entry_count: number;
}

/**
 * Workspace cost overview
 */
export interface WorkspaceCostOverview {
  workspace_id: string;
  entities_with_costs: number;
  total_monthly_cost: number;
  infrastructure_monthly: number;
  licensing_monthly: number;
  labor_monthly: number;
  support_monthly: number;
  other_monthly: number;
}

/**
 * Documentation types
 */
export type DocType = 'runbook' | 'adr' | 'readme' | 'wiki' | 'confluence' | 'notion' | 'diagram' | 'api_spec' | 'other';

/**
 * Entity documentation record
 */
export interface EntityDocumentation {
  id: string;
  workspace_id: string;
  entity_id: string;
  doc_type: DocType;
  title: string;
  url: string;
  description?: string;
  is_verified: boolean;
  verified_at?: string;
  verified_by?: string;
  verification_notes?: string;
  last_known_update?: string;
  stale_after_days: number;
  is_primary: boolean;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Parameters for creating a documentation record
 */
export interface CreateEntityDocumentationParams {
  entity_id: string;
  doc_type: DocType;
  title: string;
  url: string;
  description?: string;
  last_known_update?: string;
  stale_after_days?: number;
  is_primary?: boolean;
}

/**
 * Documentation health scores
 */
export interface DocumentationHealth {
  id: string;
  workspace_id: string;
  entity_id: string;
  overall_score: number;
  coverage_score: number;
  freshness_score: number;
  verification_score: number;
  total_docs: number;
  verified_docs: number;
  stale_docs: number;
  missing_required: number;
  required_doc_types?: DocType[];
  computed_at: string;
}

/**
 * Entity documentation summary
 */
export interface EntityDocumentationSummary {
  workspace_id: string;
  entity_id: string;
  entity_name: string;
  entity_type: EntityType;
  total_docs: number;
  verified_docs: number;
  primary_docs: number;
  stale_docs: number;
  doc_types_present: string;
  last_verified?: string;
  last_doc_update?: string;
}

/**
 * Workspace documentation overview
 */
export interface WorkspaceDocumentationOverview {
  workspace_id: string;
  entities_with_docs: number;
  total_docs: number;
  verified_docs: number;
  stale_docs: number;
  avg_health_score: number;
}

/**
 * Cost type labels with colors
 */
export const COST_TYPE_CONFIG: Record<CostType, { label: string; color: string; bgColor: string }> = {
  infrastructure: { label: 'Infrastructure', color: 'text-blue-400', bgColor: 'bg-blue-900/50' },
  licensing: { label: 'Licensing', color: 'text-purple-400', bgColor: 'bg-purple-900/50' },
  labor: { label: 'Labor', color: 'text-green-400', bgColor: 'bg-green-900/50' },
  support: { label: 'Support', color: 'text-yellow-400', bgColor: 'bg-yellow-900/50' },
  other: { label: 'Other', color: 'text-gray-400', bgColor: 'bg-gray-900/50' },
};

/**
 * Documentation type labels with icons
 */
export const DOC_TYPE_CONFIG: Record<DocType, { label: string; icon: string; color: string }> = {
  runbook: { label: 'Runbook', icon: '📋', color: 'text-red-400' },
  adr: { label: 'ADR', icon: '📝', color: 'text-blue-400' },
  readme: { label: 'README', icon: '📄', color: 'text-green-400' },
  wiki: { label: 'Wiki', icon: '📚', color: 'text-purple-400' },
  confluence: { label: 'Confluence', icon: '🔵', color: 'text-blue-400' },
  notion: { label: 'Notion', icon: '⬛', color: 'text-gray-400' },
  diagram: { label: 'Diagram', icon: '📊', color: 'text-orange-400' },
  api_spec: { label: 'API Spec', icon: '🔌', color: 'text-teal-400' },
  other: { label: 'Other', icon: '📎', color: 'text-gray-400' },
};

// ============================================================================
// PHASE 4: ADVANCED COMPLIANCE & AI FEATURES
// ============================================================================

/**
 * Evidence type for compliance
 */
export type EvidenceType = 'document' | 'screenshot' | 'log' | 'report' | 'attestation' | 'other';

/**
 * Evidence status
 */
export type EvidenceStatus = 'pending' | 'approved' | 'rejected' | 'expired';

/**
 * Compliance timeline event type
 */
export type ComplianceEventType =
  | 'assessment'
  | 'evidence_added'
  | 'evidence_expired'
  | 'status_change'
  | 'audit'
  | 'finding'
  | 'remediation'
  | 'exception';

/**
 * Event severity
 */
export type EventSeverity = 'info' | 'low' | 'medium' | 'high' | 'critical';

/**
 * AI suggestion type
 */
export type AISuggestionType =
  | 'entity_discovery'
  | 'relationship'
  | 'classification'
  | 'metadata'
  | 'compliance'
  | 'cost'
  | 'documentation';

/**
 * AI suggestion status
 */
export type AISuggestionStatus = 'pending' | 'accepted' | 'rejected' | 'expired';

/**
 * NL query type
 */
export type NLQueryType = 'search' | 'analysis' | 'comparison' | 'recommendation' | 'report';

/**
 * Compliance evidence record
 */
export interface ComplianceEvidence {
  id: string;
  workspace_id: string;
  entity_id: string;
  control_id: string;
  evidence_type: EvidenceType;
  title: string;
  description?: string;
  url?: string;
  file_path?: string;
  valid_from: string;
  valid_until?: string;
  status: EvidenceStatus;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  created_by: string;
}

/**
 * Control inheritance relationship
 */
export interface ControlInheritance {
  id: string;
  workspace_id: string;
  parent_control_id: string;
  child_control_id: string;
  inheritance_type: 'full' | 'partial' | 'conditional';
  conditions?: Record<string, unknown>;
  created_at: string;
  created_by: string;
}

/**
 * Compliance timeline event
 */
export interface ComplianceTimelineEvent {
  id: string;
  workspace_id: string;
  entity_id?: string;
  event_type: ComplianceEventType;
  title: string;
  description?: string;
  control_id?: string;
  evidence_id?: string;
  assessment_id?: string;
  previous_status?: string;
  new_status?: string;
  severity?: EventSeverity;
  event_at: string;
  created_by: string;
}

/**
 * AI suggestion record
 */
export interface AISuggestion {
  id: string;
  workspace_id: string;
  suggestion_type: AISuggestionType;
  title: string;
  description?: string;
  confidence: number;
  target_entity_id?: string;
  target_entity_name?: string;
  suggested_data: Record<string, unknown>;
  source?: string;
  source_context?: Record<string, unknown>;
  status: AISuggestionStatus;
  decision_by?: string;
  decision_at?: string;
  decision_notes?: string;
  created_at: string;
  expires_at?: string;
}

/**
 * NL query history record
 */
export interface NLQueryHistory {
  id: string;
  workspace_id: string;
  user_id: string;
  query_text: string;
  query_type: NLQueryType;
  response_summary?: string;
  result_count?: number;
  entity_ids?: string[];
  execution_time_ms?: number;
  tokens_used?: number;
  was_helpful?: boolean;
  feedback_notes?: string;
  created_at: string;
}

/**
 * Control evidence summary
 */
export interface ControlEvidenceSummary {
  workspace_id: string;
  control_id: string;
  control_name: string;
  framework: string;
  total_evidence: number;
  approved_evidence: number;
  pending_evidence: number;
  expired_evidence: number;
  latest_expiry?: string;
  next_expiry?: string;
}

/**
 * AI suggestions summary by type
 */
export interface AISuggestionsSummary {
  workspace_id: string;
  suggestion_type: AISuggestionType;
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
  avg_confidence: number;
  latest_suggestion?: string;
}

/**
 * Entity compliance health
 */
export interface EntityComplianceHealth {
  workspace_id: string;
  entity_id: string;
  entity_name: string;
  entity_type: string;
  total_assessments: number;
  avg_score?: number;
  compliance_rate?: number;
  total_evidence: number;
  approved_evidence: number;
  expired_evidence: number;
  last_assessment?: string;
  last_compliance_event?: string;
}

/**
 * Event type labels
 */
export const COMPLIANCE_EVENT_TYPE_CONFIG: Record<ComplianceEventType, { label: string; color: string; icon: string }> = {
  assessment: { label: 'Assessment', color: 'text-blue-400', icon: '📋' },
  evidence_added: { label: 'Evidence Added', color: 'text-green-400', icon: '📄' },
  evidence_expired: { label: 'Evidence Expired', color: 'text-yellow-400', icon: '⚠️' },
  status_change: { label: 'Status Change', color: 'text-purple-400', icon: '🔄' },
  audit: { label: 'Audit', color: 'text-indigo-400', icon: '🔍' },
  finding: { label: 'Finding', color: 'text-red-400', icon: '🚨' },
  remediation: { label: 'Remediation', color: 'text-teal-400', icon: '🔧' },
  exception: { label: 'Exception', color: 'text-orange-400', icon: '📝' },
};

/**
 * Severity labels
 */
export const SEVERITY_CONFIG: Record<EventSeverity, { label: string; color: string; bgColor: string }> = {
  info: { label: 'Info', color: 'text-gray-400', bgColor: 'bg-gray-900/50' },
  low: { label: 'Low', color: 'text-blue-400', bgColor: 'bg-blue-900/50' },
  medium: { label: 'Medium', color: 'text-yellow-400', bgColor: 'bg-yellow-900/50' },
  high: { label: 'High', color: 'text-orange-400', bgColor: 'bg-orange-900/50' },
  critical: { label: 'Critical', color: 'text-red-400', bgColor: 'bg-red-900/50' },
};

/**
 * AI suggestion type config
 */
export const AI_SUGGESTION_TYPE_CONFIG: Record<AISuggestionType, { label: string; color: string; description: string }> = {
  entity_discovery: { label: 'Entity Discovery', color: 'text-green-400', description: 'Discovered new entities to add' },
  relationship: { label: 'Relationship', color: 'text-blue-400', description: 'Suggested relationships between entities' },
  classification: { label: 'Classification', color: 'text-purple-400', description: 'Entity classification suggestions' },
  metadata: { label: 'Metadata', color: 'text-teal-400', description: 'Metadata updates for entities' },
  compliance: { label: 'Compliance', color: 'text-orange-400', description: 'Compliance-related suggestions' },
  cost: { label: 'Cost', color: 'text-yellow-400', description: 'Cost tracking suggestions' },
  documentation: { label: 'Documentation', color: 'text-indigo-400', description: 'Documentation improvements' },
};

// ============================================================================
// PHASE 5: REPORTING, WORKFLOW & MULTI-WORKSPACE
// ============================================================================

/**
 * Report types
 */
export type ReportType =
  | 'compliance_summary'
  | 'cost_analysis'
  | 'entity_inventory'
  | 'relationship_map'
  | 'health_dashboard'
  | 'custom';

/**
 * Report schedule
 */
export type ReportSchedule = 'daily' | 'weekly' | 'monthly' | 'quarterly';

/**
 * Report output format
 */
export type ReportOutputFormat = 'pdf' | 'csv' | 'excel' | 'json' | 'html';

/**
 * Report generation status
 */
export type ReportStatus = 'pending' | 'generating' | 'completed' | 'failed';

/**
 * Change request type
 */
export type ChangeRequestType = 'create' | 'update' | 'delete' | 'archive' | 'relationship' | 'bulk';

/**
 * Change request status
 */
export type ChangeRequestStatus =
  | 'draft'
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'implemented'
  | 'cancelled';

/**
 * Change request priority
 */
export type ChangeRequestPriority = 'low' | 'medium' | 'high' | 'critical';

/**
 * Review cycle type
 */
export type ReviewCycleType =
  | 'entity_validation'
  | 'relationship_audit'
  | 'compliance_check'
  | 'cost_review'
  | 'documentation_review'
  | 'ownership_review';

/**
 * Review cycle frequency
 */
export type ReviewCycleFrequency = 'monthly' | 'quarterly' | 'semi_annual' | 'annual';

/**
 * Review cycle status
 */
export type ReviewCycleStatus = 'not_started' | 'in_progress' | 'completed' | 'overdue';

/**
 * Cross-workspace reference type
 */
export type CrossWorkspaceRefType = 'depends_on' | 'shared_with' | 'derived_from' | 'linked_to';

/**
 * Scheduled report record
 */
export interface ScheduledReport {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  report_type: ReportType;
  schedule: ReportSchedule;
  next_run_at?: string;
  last_run_at?: string;
  recipients?: string[];
  parameters?: Record<string, unknown>;
  output_format: ReportOutputFormat;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

/**
 * Report history record
 */
export interface ReportHistory {
  id: string;
  workspace_id: string;
  scheduled_report_id?: string;
  report_type: ReportType;
  name: string;
  parameters?: Record<string, unknown>;
  output_format?: ReportOutputFormat;
  file_path?: string;
  file_size?: number;
  file_url?: string;
  status: ReportStatus;
  error_message?: string;
  generation_started_at?: string;
  generation_completed_at?: string;
  generation_time_ms?: number;
  created_at: string;
  created_by: string;
}

/**
 * Change request record
 */
export interface ChangeRequest {
  id: string;
  workspace_id: string;
  entity_id?: string;
  request_type: ChangeRequestType;
  title: string;
  description?: string;
  justification?: string;
  proposed_changes: Record<string, unknown>;
  current_state?: Record<string, unknown>;
  priority: ChangeRequestPriority;
  impact_assessment?: string;
  status: ChangeRequestStatus;
  assigned_reviewer?: string;
  reviewed_by?: string;
  reviewed_at?: string;
  review_notes?: string;
  implemented_by?: string;
  implemented_at?: string;
  implementation_notes?: string;
  submitted_at?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

/**
 * Review cycle record
 */
export interface ReviewCycle {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  review_type: ReviewCycleType;
  frequency: ReviewCycleFrequency;
  next_due_at?: string;
  last_completed_at?: string;
  entity_filter?: Record<string, unknown>;
  reviewer_ids?: string[];
  current_cycle_started_at?: string;
  current_cycle_status?: ReviewCycleStatus;
  items_to_review: number;
  items_reviewed: number;
  is_active: boolean;
  auto_create_tasks: boolean;
  created_at: string;
  updated_at: string;
  created_by: string;
}

/**
 * Webhook configuration
 */
export interface Webhook {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  url: string;
  secret?: string;
  events: string[];
  is_active: boolean;
  include_payload: boolean;
  retry_count: number;
  total_deliveries: number;
  successful_deliveries: number;
  failed_deliveries: number;
  last_delivery_at?: string;
  last_delivery_status?: string;
  created_at: string;
  updated_at: string;
  created_by: string;
}

/**
 * Workspace template
 */
export interface WorkspaceTemplate {
  id: string;
  name: string;
  description?: string;
  category?: string;
  template_data: Record<string, unknown>;
  version: string;
  is_public: boolean;
  usage_count: number;
  created_by_workspace?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Cross-workspace reference
 */
export interface CrossWorkspaceRef {
  id: string;
  source_workspace_id: string;
  source_entity_id: string;
  target_workspace_id: string;
  target_entity_id: string;
  reference_type: CrossWorkspaceRefType;
  notes?: string;
  is_bidirectional: boolean;
  requires_approval: boolean;
  approved_by?: string;
  approved_at?: string;
  created_at: string;
  created_by: string;
}

/**
 * Change request summary
 */
export interface ChangeRequestSummary {
  workspace_id: string;
  total_requests: number;
  draft_count: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
  implemented_count: number;
  avg_days_to_implement?: number;
}

/**
 * Report type configuration
 */
export const REPORT_TYPE_CONFIG: Record<ReportType, { label: string; description: string; icon: string }> = {
  compliance_summary: { label: 'Compliance Summary', description: 'Overview of compliance status', icon: '🛡️' },
  cost_analysis: { label: 'Cost Analysis', description: 'Cost breakdown and trends', icon: '💰' },
  entity_inventory: { label: 'Entity Inventory', description: 'Full list of all entities', icon: '📦' },
  relationship_map: { label: 'Relationship Map', description: 'Entity relationships visualization', icon: '🔗' },
  health_dashboard: { label: 'Health Dashboard', description: 'System health metrics', icon: '📊' },
  custom: { label: 'Custom Report', description: 'Custom report configuration', icon: '📝' },
};

/**
 * Change request status configuration
 */
export const CHANGE_REQUEST_STATUS_CONFIG: Record<ChangeRequestStatus, { label: string; color: string; bgColor: string }> = {
  draft: { label: 'Draft', color: 'text-gray-400', bgColor: 'bg-gray-900/50' },
  submitted: { label: 'Submitted', color: 'text-blue-400', bgColor: 'bg-blue-900/50' },
  under_review: { label: 'Under Review', color: 'text-purple-400', bgColor: 'bg-purple-900/50' },
  approved: { label: 'Approved', color: 'text-green-400', bgColor: 'bg-green-900/50' },
  rejected: { label: 'Rejected', color: 'text-red-400', bgColor: 'bg-red-900/50' },
  implemented: { label: 'Implemented', color: 'text-teal-400', bgColor: 'bg-teal-900/50' },
  cancelled: { label: 'Cancelled', color: 'text-gray-400', bgColor: 'bg-gray-900/50' },
};

/**
 * Review cycle type configuration
 */
export const REVIEW_CYCLE_TYPE_CONFIG: Record<ReviewCycleType, { label: string; description: string }> = {
  entity_validation: { label: 'Entity Validation', description: 'Verify entity data accuracy' },
  relationship_audit: { label: 'Relationship Audit', description: 'Review entity relationships' },
  compliance_check: { label: 'Compliance Check', description: 'Verify compliance status' },
  cost_review: { label: 'Cost Review', description: 'Review and update costs' },
  documentation_review: { label: 'Documentation Review', description: 'Check documentation freshness' },
  ownership_review: { label: 'Ownership Review', description: 'Verify ownership assignments' },
};
